import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faClipboardCheck,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  formatTaskDate,
  getTaskForwardCount,
  getTaskPriorityClasses,
  getTaskStatusClasses,
  isTaskForwardLocked,
  normalizeTaskProgressPercent,
} from "../Tasks/taskUtils";

export default function TicketTaskListModal({
  open,
  ticket,
  tasks,
  loading,
  error,
  onClose,
  onCreateTask,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex min-h-[100dvh] w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl sm:min-h-0 sm:max-h-[85vh] sm:rounded-3xl sm:border sm:border-slate-200">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {ticket?.ticketNumber ? (
                <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                  {ticket.ticketNumber}
                </span>
              ) : null}
              <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                {tasks.length} {tasks.length === 1 ? "Task" : "Tasks"}
              </span>
            </div>
            <h2 className="mt-2.5 text-lg font-semibold text-slate-900 sm:mt-3 sm:text-xl">Ticket Task List</h2>
            <p className="mt-1 text-sm leading-5 text-slate-500">
              {ticket?.subject || "Review internal task work for this support ticket."}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 sm:h-10 sm:w-10"
            aria-label="Close ticket task list"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-12 text-center text-sm text-slate-500">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading ticket tasks...
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">{error}</div>
          ) : tasks.length ? (
            <div className="space-y-2.5 sm:space-y-3">
              {tasks.map((task) => {
                const progressPercent = normalizeTaskProgressPercent(task.progressPercent);
                const forwardCount = getTaskForwardCount(task);
                const forwardLocked = isTaskForwardLocked(task);

                return (
                <div key={task.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5 sm:p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold sm:text-xs ${getTaskStatusClasses(task.status)}`}>
                          {task.status}
                        </span>
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold sm:text-xs ${getTaskPriorityClasses(task.priority)}`}>
                          {task.priority}
                        </span>
                        <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 sm:text-xs">
                          {progressPercent}% progress
                        </span>
                      </div>
                      <div className="mt-2.5 text-sm font-semibold leading-5 text-slate-900 sm:mt-3">{task.title || "Untitled task"}</div>
                      {task.description ? (
                        <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-5 text-slate-600 sm:mt-2">{task.description}</p>
                      ) : null}
                      <div className="mt-3">
                        <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 sm:text-[11px]">
                          <span>Task Progress</span>
                          <span>{progressPercent}%</span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-slate-200 sm:h-2.5">
                          <div
                            className={`h-full rounded-full transition-all ${task.status === "Completed" ? "bg-emerald-500" : "bg-slate-900"}`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                      </div>
                      <div className="mt-2.5 flex flex-wrap gap-1.5 text-[11px] sm:mt-3 sm:gap-2 sm:text-xs">
                        <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-600">
                          {forwardCount} {forwardCount === 1 ? "forward" : "forwards"}
                        </span>
                        <span className={`rounded-full px-2.5 py-1 font-semibold ${forwardLocked ? "border border-amber-200 bg-amber-100 text-amber-700" : "border border-emerald-200 bg-emerald-100 text-emerald-700"}`}>
                          {forwardLocked ? "Forward locked" : "Current owner active"}
                        </span>
                      </div>
                      <div className="mt-2.5 grid gap-1.5 text-[11px] text-slate-500 sm:mt-3 sm:flex sm:flex-wrap sm:gap-x-4 sm:gap-y-2 sm:text-xs">
                        <span>Assigned to {task.assignedEmployeeName || "Unassigned"}</span>
                        <span>Due {formatTaskDate(task.dueDate)}</span>
                        <span>Updated {formatTaskDate(task.updatedAt, true)}</span>
                      </div>
                    </div>

                    <div className="shrink-0 sm:pl-3">
                      <Link
                        to={task.assignedUserId ? `/my-tasks?userId=${encodeURIComponent(task.assignedUserId)}` : "/my-tasks"}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 sm:w-auto"
                      >
                        Open Task Desk
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                      </Link>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-12 text-center text-sm text-slate-500">
              <FontAwesomeIcon icon={faClipboardCheck} className="mr-2" />
              No task has been created from this ticket yet.
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
          <div className="text-sm leading-5 text-slate-500">
            {ticket?.ticketNumber ? `Internal task work for ${ticket.ticketNumber}` : "Internal task workspace"}
          </div>
          <div className="flex flex-col gap-2.5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-white sm:w-auto"
            >
              Close
            </button>
            <button
              type="button"
              onClick={onCreateTask}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 sm:w-auto"
            >
              <FontAwesomeIcon icon={faClipboardCheck} />
              Make Task
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
